<script setup>
/**
 * PipelineAutoAssignmentSetting — Fase 4.4 frontend
 *
 * UI for the auto-assignment feature: toggle, strategy radio,
 * agent multi-select, optional max_per_agent. Reads/writes
 * `pipeline.settings['auto_assignment']` (matched by backend
 * Pipeline#auto_assignment_* helpers + Pipelines::AutoAssignmentService).
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const props = defineProps({
  pipeline: { type: Object, required: true },
});
const emit = defineEmits(['change']);

const { t } = useI18n();
const store = useStore();

const initial = props.pipeline?.settings?.auto_assignment || {};

const enabled = ref(initial.enabled === true);
const strategy = ref(initial.strategy || 'round_robin');
const agentIds = ref(
  Array.isArray(initial.agent_ids) ? [...initial.agent_ids] : []
);
const maxPerAgent = ref(initial.max_per_agent ?? '');

const allAgents = computed(
  () => store.getters['agents/getVerifiedAgents'] || []
);

watch(
  [enabled, strategy, agentIds, maxPerAgent],
  () => {
    emit('change', {
      enabled: enabled.value,
      strategy: strategy.value,
      agent_ids: agentIds.value.map(Number).filter(Boolean),
      max_per_agent:
        maxPerAgent.value === '' ? null : Number(maxPerAgent.value),
    });
  },
  { deep: true }
);

const toggleAgent = id => {
  const idx = agentIds.value.indexOf(id);
  if (idx === -1) agentIds.value.push(id);
  else agentIds.value.splice(idx, 1);
};
</script>

<template>
  <section class="space-y-3">
    <header class="flex items-center justify-between">
      <div>
        <h4 class="text-label font-semibold text-n-slate-12">
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.TITLE') }}
        </h4>
        <p class="text-label-small text-n-slate-11 mt-0.5">
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.DESCRIPTION') }}
        </p>
      </div>
      <label class="inline-flex items-center cursor-pointer">
        <input v-model="enabled" type="checkbox" class="sr-only peer" />
        <span
          class="relative w-10 h-6 bg-n-alpha-3 peer-checked:bg-n-brand rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:size-5 after:transition-transform peer-checked:after:translate-x-4"
        />
      </label>
    </header>

    <div v-if="enabled" class="pl-1 space-y-3">
      <!-- Strategy -->
      <div>
        <label
          class="text-label-small font-medium text-n-slate-11 block mb-1.5"
        >
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.STRATEGY_LABEL') }}
        </label>
        <div class="flex gap-2">
          <label
            v-for="s in ['round_robin', 'workload']"
            :key="s"
            class="flex-1 px-3 py-2 rounded-lg border cursor-pointer transition-colors"
            :class="[
              strategy === s
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-weak hover:bg-n-alpha-1 text-n-slate-12',
            ]"
          >
            <input v-model="strategy" type="radio" :value="s" class="sr-only" />
            <div class="font-medium text-label">
              {{
                t(
                  `PIPELINE.SETTINGS.AUTO_ASSIGN.STRATEGY.${s.toUpperCase()}.LABEL`
                )
              }}
            </div>
            <div class="text-label-small text-n-slate-11 mt-0.5">
              {{
                t(
                  `PIPELINE.SETTINGS.AUTO_ASSIGN.STRATEGY.${s.toUpperCase()}.DESCRIPTION`
                )
              }}
            </div>
          </label>
        </div>
      </div>

      <!-- Agents -->
      <div>
        <label
          class="text-label-small font-medium text-n-slate-11 block mb-1.5"
        >
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.AGENTS_LABEL') }}
          <span class="text-n-slate-10">({{ agentIds.length }})</span>
        </label>
        <div
          class="max-h-40 overflow-y-auto rounded-lg border border-n-weak divide-y divide-n-weak"
        >
          <label
            v-for="agent in allAgents"
            :key="agent.id"
            class="flex items-center gap-2 px-3 py-2 hover:bg-n-alpha-1 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="agentIds.includes(agent.id)"
              class="size-4 accent-n-brand"
              @change="toggleAgent(agent.id)"
            />
            <span class="text-label text-n-slate-12 flex-1 truncate">
              {{ agent.name }}
            </span>
            <span class="text-label-small text-n-slate-10">{{
              agent.email
            }}</span>
          </label>
          <div
            v-if="!allAgents.length"
            class="px-3 py-4 text-center text-label-small text-n-slate-10"
          >
            {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.NO_AGENTS') }}
          </div>
        </div>
      </div>

      <!-- Max per agent -->
      <div>
        <label
          class="text-label-small font-medium text-n-slate-11 block mb-1.5"
        >
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.MAX_PER_AGENT_LABEL') }}
        </label>
        <input
          v-model="maxPerAgent"
          type="number"
          min="0"
          :placeholder="
            t('PIPELINE.SETTINGS.AUTO_ASSIGN.MAX_PER_AGENT_PLACEHOLDER')
          "
          class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-label text-n-slate-12 focus:outline-none focus:border-n-brand"
        />
        <p class="text-label-small text-n-slate-10 mt-1">
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.MAX_PER_AGENT_HINT') }}
        </p>
      </div>
    </div>
  </section>
</template>
